import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import { Pie, PieLegend, TextSkeleton, type CardProps, type FilterLink, type PieSlice } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { GROUND_CAUSE_VIEWS } from '@chess/api-mistakes';
import '@chess/api-ending-case/colors.css';
import { wentWrongOf, type LostGroundResult } from './LostGroundAggregator.ts';
import { LOST_GROUND_HELP } from './help.tsx';
import './lost-ground.css';

// How you lost ground: the period's middlegames that went wrong (Blown
// advantage or Behind in Middlegame results), each by what cost the most win
// chance over the whole middlegame: Tactic, Missed chance, Time, Positional,
// in the losses' red band. Every slice and legend entry opens exactly those
// games in the library (`ground-cause`); the "?" opens the help topic that
// explains the rule. Drawn from props only: the card's body, which the
// section draws in the dashboard's frame (`LOST_GROUND_CARD`, `registerCard`).

export type LostGroundCardProps = CardProps<{ result: LostGroundResult }, {
  /** Opens the library filtered to a slice's games. */
  link?: FilterLink;
}>;

const SIZE = 96;
const middlegames = (n: number) => `${n} ${n === 1 ? 'middlegame' : 'middlegames'}`;

function slicesOf(r: LostGroundResult): PieSlice[] {
  return GROUND_CAUSE_VIEWS.map((v) => ({
    key: v.cause,
    label: v.label,
    count: r.byCause[v.cause],
    colour: v.colour,
    title: `${v.description} · opens those games`,
    filter: { groundCause: v.cause },
  }));
}

const LoadingBody: React.FC = () => (
  <div className="donut-wrap">
    <Skeleton loading variant="circular" width={`${SIZE / 16}rem`} height={`${SIZE / 16}rem`} label="Loading how you lost ground" />
    <ul className="legend">
      {GROUND_CAUSE_VIEWS.map((v) => (
        <li key={v.cause}>
          <span className="legend-swatch" style={{ background: v.colour }} />
          {v.label}
          <span className="legend-value">
            <Skeleton loading inline variant="text" shape="12 · 34%" />
          </span>
        </li>
      ))}
    </ul>
  </div>
);

/** The frame's title and help topic: the registration's and the stories'. */
export const LOST_GROUND_CARD = { title: 'How you lost ground', help: LOST_GROUND_HELP };
const ASIDE = 'what cost the most';

export const LostGroundCard: React.FC<LostGroundCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <div className="lost-ground-body">
          <LoadingBody />
        </div>
        <CardNote>
          <TextSkeleton sample="12 middlegames went wrong" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  const total = wentWrongOf(result);
  if (!total)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>No middlegames went wrong in the last 30 days.</CardEmpty>
      </>
    );
  const slices = slicesOf(result);
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <div className="lost-ground-body">
        <div className="donut-wrap">
          <Pie slices={slices} size={SIZE} link={link} />
          <PieLegend slices={slices} link={link} />
        </div>
      </div>
      <CardNote>{`${middlegames(total)} went wrong`}</CardNote>
    </>
  );
};
